/**
 * Availability API Types & Schemas
 */

import { z } from '@ufc/core'

// ============================================================================
// Factory DTO Schema
// ============================================================================

export const factoryDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  code: z.string(),
  // Strapi returns name as string when using locale parameter
  name: z.string(),
  factoryStatus: z.enum(['active', 'inactive']).optional(),
})

// ============================================================================
// Variant DTO Schema
// ============================================================================

export const variantDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  variantCode: z.string(),
  // Strapi returns name as string when using locale parameter
  name: z.string(),
  packaging: z.enum(['bags', 'bulk']).nullable().optional(),
  isActive: z.boolean().optional().default(true),
  sku: z.object({
    id: z.union([z.string(), z.number()]),
    documentId: z.string(),
    code: z.string().optional(),
    // Strapi returns name as string when using locale parameter
    name: z.string().optional(),
  }).optional(),
})

// ============================================================================
// Factory Variant Availability DTO Schema
// ============================================================================

export const availabilityRecordDtoSchema = z.object({
  id: z.union([z.string(), z.number()]),
  documentId: z.string(),
  factory: z.object({
    id: z.union([z.string(), z.number()]),
    documentId: z.string(),
  }).optional(),
  variant: z.object({
    id: z.union([z.string(), z.number()]),
    documentId: z.string(),
  }).optional(),
  isAvailable: z.boolean().default(true),
  leadTimeDays: z.number().optional().nullable(),
  notes: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional().nullable(),
})

// ============================================================================
// Inferred Types
// ============================================================================

export type FactoryDto = z.infer<typeof factoryDtoSchema>
export type VariantDto = z.infer<typeof variantDtoSchema>
export type AvailabilityRecordDto = z.infer<typeof availabilityRecordDtoSchema>

// ============================================================================
// Request Types
// ============================================================================

export interface CreateAvailabilityRequest {
  factory: string // documentId
  variant: string // documentId
  isAvailable: boolean
}

export interface UpdateAvailabilityRequestDto {
  isAvailable: boolean
}

export interface BulkUpdateAvailabilityRequestDto {
  changes: Array<{
    factoryDocumentId: string
    variantDocumentId: string
    isAvailable: boolean
  }>
}

// ============================================================================
// API Endpoints
// ============================================================================

export const AVAILABILITY_ENDPOINTS = {
  FACTORIES: '/api/factories',
  VARIANTS: '/api/variants',
  AVAILABILITY: '/api/factory-variant-availabilities',
  AVAILABILITY_BY_ID: (documentId: string) => `/api/factory-variant-availabilities/${documentId}`,
} as const
